import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AdminPageHeader from "@/admin/components/AdminPageHeader";
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Copy,
  Filter,
  Layers,
  Mail,
  Search,
  Tag,
} from "lucide-react";

const STATUSES = ["all", "new", "contacted", "qualified", "proposal", "won", "lost"];
const STATUS_LABEL = {
  all: "Toate statusurile",
  new: "Noi",
  contacted: "Contactate",
  qualified: "Calificate",
  proposal: "Ofertă / negociere",
  won: "Câștigate",
  lost: "Pierdute",
};

const PRIORITIES = ["all", "low", "normal", "high"];
const PRIORITY_LABEL = {
  all: "Toate prioritățile",
  low: "Scăzută",
  normal: "Normală",
  high: "Ridicată",
};

function badgeClass(status) {
  if (status === "won") return "bg-emerald-100 text-emerald-800 ring-emerald-200";
  if (status === "lost") return "bg-rose-100 text-rose-800 ring-rose-200";
  if (status === "proposal") return "bg-violet-100 text-violet-800 ring-violet-200";
  if (status === "qualified") return "bg-indigo-100 text-indigo-800 ring-indigo-200";
  if (status === "contacted") return "bg-amber-100 text-amber-900 ring-amber-200";
  return "bg-[#fbd9e5] text-[#3e2723] ring-[#3e2723]/15";
}

function priorityClass(p) {
  if (p === "high") return "bg-rose-50 text-rose-800 ring-rose-200";
  if (p === "low") return "bg-slate-100 text-slate-600 ring-slate-200";
  return "bg-sky-50 text-sky-800 ring-sky-200";
}

/** Parse backend datetime as UTC for display in locale */
function formatLeadWhen(iso) {
  if (!iso) return "";
  const s = String(iso).trim();
  const d = s.endsWith("Z") ? new Date(s) : new Date(s.includes("T") ? s : s.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("ro-RO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function relativeHint(iso) {
  if (!iso) return "";
  const s = String(iso).trim();
  const d = s.endsWith("Z") ? new Date(s) : new Date(s.includes("T") ? s : s.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 24) return h <= 0 ? "Acum" : `${h}h`;
  const days = Math.floor(h / 24);
  return `${days} z`;
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    /* ignore */
  }
}

export default function AdminLeads() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState(params.get("status") || "all");
  const [priority, setPriority] = useState(params.get("priority") || "all");
  const [page, setPage] = useState(Math.max(1, Number(params.get("page") || 1)));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [copied, setCopied] = useState("");

  const pageSize = 20;

  const selected = useMemo(() => items.find((x) => x.id === selectedId) || null, [items, selectedId]);

  const syncUrl = useCallback(
    (next) => {
      const p = new URLSearchParams();
      if (next.q) p.set("q", next.q);
      if (next.status && next.status !== "all") p.set("status", next.status);
      if (next.priority && next.priority !== "all") p.set("priority", next.priority);
      if (next.page > 1) p.set("page", String(next.page));
      setParams(p, { replace: true });
    },
    [setParams]
  );

  const load = useCallback(
    async (next) => {
      setLoading(true);
      setError("");
      setMsg("");
      try {
        const u = new URL("/api/admin/leads.php", window.location.origin);
        u.searchParams.set("page", String(next.page));
        u.searchParams.set("page_size", String(pageSize));
        if (next.q) u.searchParams.set("q", next.q);
        if (next.status && next.status !== "all") u.searchParams.set("status", next.status);
        if (next.priority && next.priority !== "all") u.searchParams.set("priority", next.priority);

        const r = await fetch(u.toString(), { credentials: "include", cache: "no-store" });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || !j.ok) {
          setError(j.detail || "Nu am putut încărca lead-urile.");
          return;
        }
        const list = j.items || [];
        setItems(list);
        setTotal(j.total || 0);
        setSelectedId((curr) => {
          if (!curr && list.length) return list[0].id;
          if (curr && !list.some((x) => x.id === curr)) return list[0]?.id ?? null;
          return curr;
        });
        syncUrl(next);
      } catch {
        setError("Eroare de rețea.");
      } finally {
        setLoading(false);
      }
    },
    [pageSize, syncUrl]
  );

  useEffect(() => {
    load({ q, status, priority, page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const onApplyFilters = () => {
    setPage(1);
    load({ q, status, priority, page: 1 });
  };

  const onSaveSelected = async (patch) => {
    if (!selected) return;
    setSaving(true);
    setMsg("");
    try {
      const r = await fetch("/api/admin/leads.php", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id: selected.id, ...patch }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setMsg(j.detail || "Update eșuat.");
        return;
      }
      setItems((arr) => arr.map((it) => (it.id === selected.id ? j.item : it)));
      setMsg("Lead actualizat.");
    } catch {
      setMsg("Eroare de rețea.");
    } finally {
      setSaving(false);
    }
  };

  const onDeleteSelected = async () => {
    if (!selected) return;
    if (!window.confirm(`Ștergi lead-ul de la ${selected.name}? Acțiunea nu poate fi anulată.`)) return;
    setSaving(true);
    setMsg("");
    try {
      const r = await fetch("/api/admin/leads.php", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id: selected.id }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setMsg(j.detail || "Ștergere eșuată.");
        return;
      }
      setItems((arr) => arr.filter((it) => it.id !== selected.id));
      setTotal((n) => Math.max(0, n - 1));
      setSelectedId(null);
      setMsg("Lead șters.");
    } catch {
      setMsg("Eroare de rețea.");
    } finally {
      setSaving(false);
    }
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="mx-auto min-w-0 max-w-7xl space-y-5 overflow-x-clip pb-4">
      <AdminPageHeader
        eyebrow="CRM"
        title="Lead inbox"
        description="Gestionează pipeline-ul: status, prioritate, follow-up și note interne. Folosește filtrele și paginarea pentru volume mari."
      />

      <section className="rounded-2xl border border-[#3e2723]/10 bg-white p-4 shadow-[0_8px_30px_-18px_rgba(62,39,35,0.18)] sm:p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:gap-4">
          <label className="min-w-0 flex-1 xl:max-w-md">
            <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#3e2723]/50">
              <Search className="h-3.5 w-3.5" /> Căutare
            </span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#3e2723]/35" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onApplyFilters()}
                placeholder="Nume, email, telefon, mesaj…"
                className="w-full rounded-xl border border-[#3e2723]/12 bg-[#faf8f8] py-2.5 pl-9 pr-3 text-sm text-[#3e2723] outline-none ring-[#3e2723]/10 focus:ring-2"
              />
            </div>
          </label>

          <div className="flex flex-wrap gap-3">
            <label className="min-w-[160px]">
              <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#3e2723]/50">
                <Layers className="h-3.5 w-3.5" /> Status
              </span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full rounded-xl border border-[#3e2723]/12 bg-[#faf8f8] px-3 py-2.5 text-sm text-[#3e2723]"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            </label>

            <label className="min-w-[160px]">
              <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#3e2723]/50">
                <Tag className="h-3.5 w-3.5" /> Prioritate
              </span>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full rounded-xl border border-[#3e2723]/12 bg-[#faf8f8] px-3 py-2.5 text-sm text-[#3e2723]"
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_LABEL[p]}
                  </option>
                ))}
              </select>
            </label>

            <div className="flex flex-1 items-end pb-0.5 xl:justify-end">
              <button
                type="button"
                onClick={onApplyFilters}
                className="inline-flex items-center gap-2 rounded-full bg-[#3e2723] px-5 py-2.5 text-sm font-semibold text-[#fbd9e5] shadow-sm transition hover:bg-[#2e1e1b]"
              >
                <Filter className="h-4 w-4" /> Aplică filtre
              </button>
            </div>
          </div>
        </div>
      </section>

      {error ? <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}

      <section className="grid gap-5 lg:grid-cols-12">
        <article className="flex flex-col rounded-2xl border border-[#3e2723]/10 bg-white shadow-sm lg:col-span-5">
          <div className="border-b border-[#3e2723]/08 px-4 py-3">
            <p className="text-xs font-medium text-[#3e2723]/55">
              {total} lead{total === 1 ? "" : "-uri"}
              {loading ? " · se încarcă…" : ""}
            </p>
          </div>
          <div className="max-h-[min(62vh,560px)] flex-1 overflow-y-auto p-2">
            {loading ? (
              <p className="p-4 text-sm text-[#3e2723]/60">Se încarcă…</p>
            ) : items.length === 0 ? (
              <p className="p-4 text-sm text-[#3e2723]/60">Nu există lead-uri pentru filtrele curente.</p>
            ) : (
              items.map((lead) => {
                const pri = lead.priority || "normal";
                const rel = relativeHint(lead.created_at);
                return (
                  <button
                    key={lead.id}
                    type="button"
                    onClick={() => setSelectedId(lead.id)}
                    className={`mb-2 w-full rounded-xl border px-3 py-3 text-left transition ${
                      selectedId === lead.id
                        ? "border-[#3e2723]/30 bg-[#fbd9e5]/40 shadow-sm ring-1 ring-[#3e2723]/10"
                        : "border-[#3e2723]/08 bg-[#faf8f8]/80 hover:border-[#3e2723]/18 hover:bg-[#fbd9e5]/15"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-[#3e2723]">{lead.name}</p>
                        <p className="truncate text-xs text-[#3e2723]/60">{lead.email}</p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ring-1 ${badgeClass(
                          lead.status
                        )}`}
                      >
                        {STATUS_LABEL[lead.status] || lead.status}
                      </span>
                    </div>
                    {lead.service ? (
                      <p className="mt-2 truncate text-[11px] font-medium text-[#3e2723]/55">
                        Interes: <span className="text-[#3e2723]/80">{lead.service}</span>
                      </p>
                    ) : null}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ring-1 ${priorityClass(
                          pri
                        )}`}
                      >
                        {PRIORITY_LABEL[pri] || pri}
                      </span>
                      <span className="text-[10px] font-medium text-[#3e2723]/45" title={formatLeadWhen(lead.created_at)}>
                        {rel ? `${rel} · ` : ""}
                        {formatLeadWhen(lead.created_at)}
                      </span>
                    </div>
                    <p className="mt-2 line-clamp-2 text-[11px] leading-snug text-[#3e2723]/55">{lead.message}</p>
                  </button>
                );
              })
            )}
          </div>

          <div className="flex items-center justify-between border-t border-[#3e2723]/10 px-3 py-3 text-xs text-[#3e2723]/65">
            <span>
              Pagina {page} / {pages}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => {
                  const next = Math.max(1, page - 1);
                  setPage(next);
                  load({ q, status, priority, page: next });
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#3e2723]/15 disabled:opacity-35"
                aria-label="Pagina anterioară"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={page >= pages}
                onClick={() => {
                  const next = Math.min(pages, page + 1);
                  setPage(next);
                  load({ q, status, priority, page: next });
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#3e2723]/15 disabled:opacity-35"
                aria-label="Pagina următoare"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </article>

        <article className="rounded-2xl border border-[#3e2723]/10 bg-white p-0 shadow-sm lg:col-span-7">
          {!selected ? (
            <div className="p-8 text-center text-sm text-[#3e2723]/60">Selectează un lead din listă.</div>
          ) : (
            <LeadDetail
              lead={selected}
              onSave={onSaveSelected}
              onDelete={onDeleteSelected}
              saving={saving}
              onCopied={(label) => {
                setCopied(label);
                setTimeout(() => setCopied(""), 2000);
              }}
              copied={copied}
            />
          )}
          {msg ? (
            <div className="border-t border-[#3e2723]/08 px-5 py-3">
              <p className="rounded-xl bg-[#f0ebe9] px-3 py-2 text-sm text-[#3e2723]/85">{msg}</p>
            </div>
          ) : null}
        </article>
      </section>
    </div>
  );
}

function LeadDetail({ lead, onSave, onDelete, saving, onCopied, copied }) {
  const [status, setStatus] = useState(lead.status || "new");
  const [priority, setPriority] = useState(lead.priority || "normal");
  const [nextFollow, setNextFollow] = useState(lead.next_follow_up_at ? String(lead.next_follow_up_at).slice(0, 16) : "");
  const [notes, setNotes] = useState(lead.notes || "");

  useEffect(() => {
    setStatus(lead.status || "new");
    setPriority(lead.priority || "normal");
    setNextFollow(lead.next_follow_up_at ? String(lead.next_follow_up_at).slice(0, 16) : "");
    setNotes(lead.notes || "");
  }, [lead.id, lead.status, lead.priority, lead.next_follow_up_at, lead.notes]);

  const stOptions = STATUSES.filter((x) => x !== "all");
  const prOptions = PRIORITIES.filter((x) => x !== "all");

  return (
    <div className="divide-y divide-[#3e2723]/08">
      <div className="p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-[#3e2723]">{lead.name}</h2>
            <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-[#3e2723]/70">
              <span className="inline-flex items-center gap-1">
                <Mail className="h-3.5 w-3.5 opacity-60" />
                {lead.email}
              </span>
              {lead.phone ? <span>· {lead.phone}</span> : null}
            </p>
            {lead.service ? (
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#3e2723]/06 px-3 py-1 text-xs font-medium text-[#3e2723]/85">
                <Tag className="h-3 w-3 opacity-70" />
                {lead.service}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#3e2723]/15 bg-white px-3 py-1.5 text-xs font-semibold text-[#3e2723]/80 hover:bg-[#faf8f8]"
              onClick={() => {
                copyText(lead.email);
                onCopied("email");
              }}
            >
              <Copy className="h-3.5 w-3.5" />
              {copied === "email" ? "Copiat" : "Copiază email"}
            </button>
            {lead.phone ? (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#3e2723]/15 bg-white px-3 py-1.5 text-xs font-semibold text-[#3e2723]/80 hover:bg-[#faf8f8]"
                onClick={() => {
                  copyText(lead.phone);
                  onCopied("phone");
                }}
              >
                <Copy className="h-3.5 w-3.5" />
                {copied === "phone" ? "Copiat" : "Copiază telefon"}
              </button>
            ) : null}
          </div>
        </div>
        <p className="mt-3 text-xs text-[#3e2723]/50">
          Primit: <time dateTime={lead.created_at}>{formatLeadWhen(lead.created_at)}</time> (UTC afișat în local)
        </p>
      </div>

      <div className="p-5">
        <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#3e2723]/45">Pipeline</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-[#3e2723]/55">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-xl border border-[#3e2723]/12 bg-[#faf8f8] px-3 py-2.5 text-sm text-[#3e2723]"
            >
              {stOptions.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-[#3e2723]/45">De la „Noi” până la câștig sau pierdere.</p>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-[#3e2723]/55">Prioritate</span>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full rounded-xl border border-[#3e2723]/12 bg-[#faf8f8] px-3 py-2.5 text-sm text-[#3e2723]"
            >
              {prOptions.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABEL[p]}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-[#3e2723]/45">Folosit la sortare mentală și filtrare în inbox.</p>
          </label>
        </div>

        <label className="mt-4 block">
          <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-[#3e2723]/55">
            <CalendarClock className="h-3.5 w-3.5" />
            Următor follow-up (opțional)
          </span>
          <input
            type="datetime-local"
            value={nextFollow}
            onChange={(e) => setNextFollow(e.target.value)}
            className="w-full max-w-md rounded-xl border border-[#3e2723]/12 bg-[#faf8f8] px-3 py-2.5 text-sm"
          />
        </label>
      </div>

      <div className="p-5">
        <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#3e2723]/45">Mesajul trimis de client</h3>
        <div className="rounded-xl border border-[#3e2723]/10 bg-[#faf8f8] p-4 text-sm leading-relaxed text-[#3e2723]/90 whitespace-pre-wrap">
          {lead.message}
        </div>
      </div>

      <div className="p-5">
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-[#3e2723]/55">Note interne (doar admin)</span>
          <textarea
            rows={5}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-xl border border-[#3e2723]/12 bg-[#faf8f8] px-3 py-2.5 text-sm text-[#3e2723] outline-none focus:ring-2 focus:ring-[#3e2723]/15"
            placeholder="Ex.: buget, obiective, următor apel, link call…"
          />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-3 p-5">
        <button
          type="button"
          disabled={saving}
          onClick={() => onSave({ status, priority, next_follow_up_at: nextFollow || null, notes })}
          className="rounded-full bg-[#3e2723] px-8 py-3 text-sm font-semibold text-[#fbd9e5] shadow-sm transition hover:bg-[#2e1e1b] disabled:opacity-60"
        >
          {saving ? "Se salvează…" : "Salvează modificările"}
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={onDelete}
          className="rounded-full border border-red-200 bg-white px-6 py-3 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-60"
        >
          Șterge lead
        </button>
      </div>
    </div>
  );
}
